import { Link, useForm } from '@inertiajs/react'
import { AuthLayout } from '@/components/layout/AuthLayout'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import { Notice } from '@/components/ui/Notice'

interface Props {
  reset_password_token: string
}

export default function ResetPassword({ reset_password_token }: Props) {
  // The token rides in the form data so Devise's error for it (an expired, used or
  // tampered link) arrives as a typed field error rather than one nothing shows.
  const form = useForm({ password: '', password_confirmation: '', reset_password_token })
  const linkRefused = Boolean(form.errors.reset_password_token)

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    form.transform((data) => ({ user: data }))
    form.put('/users/password')
  }

  return (
    <AuthLayout>
      <div className="w-full max-w-sm">

        {/* Ornament */}
        <div className="flex items-center justify-center mb-10" aria-hidden="true">
          <div className="h-px w-12 bg-border-divider" />
          <span className="mx-4 font-serif text-sm text-label italic">new beginning</span>
          <div className="h-px w-12 bg-border-divider" />
        </div>

        {/* Heading */}
        <div className="text-center mb-10">
          <h1 className="font-serif text-3xl font-medium text-heading mb-2">
            Choose a new password
          </h1>
          {!linkRefused && (
            <p className="text-sm text-muted font-light">
              Make it something you'll remember
            </p>
          )}
        </div>

        {/* A refused link cannot be retried from here, so the form gives way to the way out. */}
        {linkRefused && (
          <div className="flex flex-col gap-7">
            <Notice tone="alert" title="This reset link no longer works">
              It may have expired or already been used. Ask for a new one and we&rsquo;ll email
              you a fresh link.
            </Notice>
            <Button href="/users/password/new" className="w-full text-center">
              Request a new link
            </Button>
          </div>
        )}

        {/* Form */}
        {!linkRefused && <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-7">
          <Input
            id="password"
            label="New password"
            type="password"
            autoComplete="new-password"
            value={form.data.password}
            onChange={e => form.setData('password', e.target.value)}
            error={form.errors.password}
          />

          <Input
            id="password_confirmation"
            label="Confirm new password"
            type="password"
            autoComplete="new-password"
            value={form.data.password_confirmation}
            onChange={e => form.setData('password_confirmation', e.target.value)}
            error={form.errors.password_confirmation}
          />

          <Button type="submit" variant="filled" disabled={form.processing} className="w-full mt-1">
            {form.processing ? 'Updating…' : 'Update password'}
          </Button>
        </form>}

        {/* Footer link */}
        <div className="mt-10 pt-8 border-t border-border-divider text-center">
          <p className="text-xs text-muted font-light">
            <Link
              href="/users/sign_in"
              className="text-body hover:text-heading transition-colors duration-200 underline underline-offset-2 decoration-border-divider hover:decoration-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 rounded"
            >
              Back to sign in
            </Link>
          </p>
        </div>

      </div>
    </AuthLayout>
  )
}
